import { useCallback, useEffect, useRef } from "react";
import { animated, useSprings, SpringValue } from "@react-spring/web";
import { useSpringCarousel } from "react-spring-carousel";
import { AspectRatio } from "react-aspect-ratio";
import { models } from "../../data";
import "./Models.css";
import useMediaQuery from "../../hooks/useMediaQuery";

const Card = ({
  index,
  label,
  image,
  scale,
  translateX,
  opacity,
  text,
  onClick,
}: {
  index: number;
  label: string;
  image: string;
  scale: SpringValue<number>;
  translateX: SpringValue<number>;
  opacity: SpringValue<number>;
  text: SpringValue<number>;
  onClick: (index: number) => void;
}) => {
  const md = useMediaQuery("(min-width: 768px)");
  return (
    <animated.div
      role="button"
      className="card flex flex-col items-center w-full flex-1 xl:pb-4"
      style={{
        scale,
        translateX: md
          ? undefined
          : translateX.to([-1, 0, 1], ["-50%", "0%", "50%"]),
      }}
      onClick={(e) => {
        e.preventDefault();
        onClick(index);
      }}
    >
      <animated.div style={{ opacity: text }} className="fan-shadow" />
      <AspectRatio
        ratio="0.6"
        style={{
          width: "100%",
          height: md ? "calc(100% - 45px)" : "calc(100% - 27px)",
        }}
      >
        <animated.div
          className="image w-full"
          style={{
            height: "90%",
            opacity,
            backgroundImage: `url(${image})`,
            top: "unset",
            bottom: 0,
          }}
        />
      </AspectRatio>
      <animated.h4 style={{ opacity: text }}>{label}</animated.h4>
    </animated.div>
  );
};

const Light = () => {
  return (
    <div className="light-container">
      <div className="light"></div>
    </div>
  );
};

const ModelsSection = ({ active }: { active: number }) => {
  const timerIDRef = useRef<number | null>(null);
  const [items, api] = useSprings(models.length, (i) => {
    return {
      opacity: i === 0 ? 1 : 0.5,
      text: i === 0 ? 1 : 0,
      scale: i === 0 ? 1 : 0.5,
      translateX: i === 1 ? -1 : i === models.length - 1 ? 1 : 0,
      config: {
        decay: 0,
      },
    };
  });

  const onCardClickRef = useRef((index: number) => {});

  const onCardClick = (index: number) => {
    onCardClickRef.current(index);
  };

  const {
    carouselFragment,
    useListenToCustomEvent,
    slideToPrevItem,
    slideToNextItem,
    getCurrentActiveItem,
    getIsAnimating,
    getIsNextItem,
    getIsPrevItem,
  } = useSpringCarousel({
    withLoop: true,
    itemsPerSlide: 3,
    initialStartingPosition: "center",
    items: models.map(({ name, short, image }, i) => {
      return {
        id: name,
        renderItem: (
          <Card
            index={i}
            label={short}
            image={image}
            {...items[i]}
            onClick={onCardClick}
          />
        ),
      };
    }),
    touchAction: "pan-y",
  });

  onCardClickRef.current = (index: number) => {
    if (getIsAnimating() || !getCurrentActiveItem) return;
    const currentActiveItem = getCurrentActiveItem().index;
    if (currentActiveItem === index) {
      const elem = document.querySelector("#section-features");
      if (elem) {
        window.scrollTo({
          top: elem.getBoundingClientRect().top + window.scrollY,
          behavior: "smooth",
        });
      }
    } else if (getIsPrevItem(models[index].name)) {
      slideToPrevItem();
    } else if (getIsNextItem(models[index].name)) {
      slideToNextItem();
    }
  };

  const startTimer = useCallback(() => {
    timerIDRef.current = window.setTimeout(() => {
      slideToNextItem();
    }, 5000);
  }, [slideToNextItem]);

  const clearTimer = useCallback(() => {
    if (timerIDRef.current) {
      clearTimeout(timerIDRef.current);
    }
  }, []);

  useListenToCustomEvent((data) => {
    if (data.eventName === "onSlideStartChange") {
      const activeItem = data.nextItem;
      api.start((i) => {
        const active = i === activeItem.index;
        const beforeActive =
          i === (activeItem.index + 1 + models.length) % models.length;
        const afterActive =
          i === (activeItem.index - 1 + models.length) % models.length;
        const opacity = active ? 1 : 0.5;
        const scale = active ? 1 : 0.5;
        const text = active ? 1 : 0;
        const translateX = beforeActive ? -1 : afterActive ? 1 : 0;
        return { opacity, scale, text, translateX };
      });

      clearTimer();
      startTimer();
    }
  });

  useEffect(() => {
    startTimer();
    return () => {
      clearTimer();
    };
  }, [clearTimer, startTimer]);

  return (
    <section className="section-models flex flex-col">
      <Light />
      <div className="title flex items-center justify-center">
        <h1 className="text-center font-medium">
          หลากหลายรุ่น{" "}
          <span className="block md:inline font-extralight">ให้คุณเลือก</span>
        </h1>
      </div>

      <div className="w-full overflow-hidden">
        <div className="carousel">
          <div
            role="button"
            className="marker marker-left"
            onClick={slideToPrevItem}
          >
            {"►"}
          </div>
          {carouselFragment}
          <div
            role="button"
            className="marker marker-right"
            onClick={slideToNextItem}
          >
            {"►"}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModelsSection;
